import ThCalendarDay from "./ThCalendarDay";
import type * as type from "@vTypes";
import { images } from "@img/imagesMap";
import type { NurseResponse } from "@vTypes/nurse-type.ts";
import { useState } from "react";
import { cn } from "@utils/cn.ts";
import { WEEKDAY_SHORT_SUN_FIRST } from "@utils/dayUtill";

import { toggleSortGroupedByNurseRole } from "@utils/common-util.ts";
import { NURSE_LIST_COLUMNS } from "@components/feature/rosterTable/script";
import RosterGrid from "@components/feature/roster_create/components/roster-grid.tsx";
import useRosterConfig from "@/hooks/useRosterConfig";
import type { PrevTailData } from "@/hooks/roster-craete/useRosterPrevTail";

type TableHeaderProps<T extends NurseResponse> = {
  /** 미지정 시 ROSTER_CREATE (정렬/스타일 분기) */
  type?: "ROSTER_CREATE";
  defaultShiftCode: type.availableShifts[]; // 근무표 보기에 꼭 필요
  currentYear: number;
  currentMonth: number;
  holidayDates: string[];
  copyNurses: T[];
  setCopyNurses: React.Dispatch<React.SetStateAction<T[]>>;
  prevTailData?: PrevTailData | null; // 전월 말일 근무표 데이터
  /** false: 날짜 헤더 클릭으로 열 선택 안 함(호버만) */
  selectionFocusOnClick?: boolean;
};

function TableHeader<T extends NurseResponse>({
  type = "ROSTER_CREATE",
  defaultShiftCode,
  currentYear,
  currentMonth,
  copyNurses,
  setCopyNurses,
  holidayDates,
  prevTailData,
  selectionFocusOnClick = true,
}: TableHeaderProps<T>) {
  const [sortState, setSortState] = useState<{
    key: string;
    order: "asc" | "desc";
  }>({
    key: "experience",
    order: "desc",
  });

  const handleSort = (key: string) => {
    if (!copyNurses) return;

    // 다른 키로 변경 시 desc로 시작하도록 하기 위해 asc로 설정 (toggleSort 내부 로직에 따름)
    const isNewKey = sortState.key !== key;
    const currentOrder = isNewKey ? "asc" : sortState.order;

    const { sortedData, nextOrder } = toggleSortGroupedByNurseRole(
      copyNurses,
      key as keyof T,
      currentOrder
    );

    setCopyNurses(sortedData);
    setSortState({ key, order: nextOrder });
  };

  const lastDay = new Date(currentYear, currentMonth, 0).getDate();
  const firstDayFactor = new Date(currentYear, currentMonth - 1, 1).getDay();
  const formatDate = (year: number, month: number, day: number) => {
    const mm = String(month).padStart(2, "0"); // '02'
    const dd = String(day).padStart(2, "0"); // '06'
    return `${year}-${mm}-${dd}`;
  };

  const { data: serverConfig } = useRosterConfig();

  const showLevel = serverConfig?.show_level;
  const showPreceptor = serverConfig?.show_preceptor;

  if (!defaultShiftCode || defaultShiftCode.length === 0) return;

  return (
    <RosterGrid hasPrevTail={!!prevTailData}>
      <div className={`flex items-center flex-shrink-0 gap-0 justify-start `}>
        <div
          className="flex-shrink-0 text-center"
          style={{ width: NURSE_LIST_COLUMNS.handle }}
        >
          <div className="nurseCount padding_hover_bg_vertical">
            <img src={images.user} alt="" className="" />
            {copyNurses.length}
          </div>
        </div>

        <div
          className={cn(
            "flex-shrink-0 text-sm text-[#939399] text-center",
            type === "ROSTER_CREATE" && "cursor-pointer underline"
          )}
          style={{ width: NURSE_LIST_COLUMNS.name }}
          onClick={() => handleSort("name")}
        >
          <span>이름</span>
        </div>

        {showPreceptor && (
          <div
            className="flex-shrink-0 text-sm text-[#939399] text-center"
            style={{ width: NURSE_LIST_COLUMNS.preceptor }}
          >
            프셉
          </div>
        )}

        {/* <div
          className="flex-shrink-0 text-sm text-[#939399] text-center relative"
          style={{ width: NURSE_LIST_COLUMNS.age }}
        >
          나이{" "}
          <span className="text-xs absolute bottom-0 -right-[6px]">(만)</span>
        </div> */}

        <div
          className="flex-shrink-0 text-sm text-[#939399] text-center cursor-pointer group underline"
          style={{ width: NURSE_LIST_COLUMNS.grade }}
          onClick={() => handleSort("grade")}
        >
          <span>Grade</span>
        </div>

        {showLevel && (
          <div
            className="flex-shrink-0 text-sm text-[#939399] text-center"
            style={{
              width: NURSE_LIST_COLUMNS.role,
            }}
          >
            직군
          </div>
        )}

        <div
          className={cn(
            "flex-shrink-0 text-sm text-[#939399] text-center",
            type === "ROSTER_CREATE" && "cursor-pointer underline"
          )}
          style={{ width: NURSE_LIST_COLUMNS.experience }}
          onClick={() => {
            handleSort("experience");
          }}
        >
          <span>경력</span>
        </div>
      </div>

      <div className={cn("flex", !prevTailData && "ml-[1px]")}>
        {/* 전월 말일 데이터 표시 - 사진처럼 포괄적으로 감쌉니다 */}
        {prevTailData?.nurses &&
          prevTailData.nurses.length > 0 &&
          (() => {
            const prevMonth =
              prevTailData.prev_month ??
              (currentMonth === 1 ? 12 : currentMonth - 1);
            const prevYear =
              prevTailData.prev_year ??
              (currentMonth === 1 ? currentYear - 1 : currentYear);
            const prevMonthLastDay = new Date(prevYear, prevMonth, 0).getDate();

            const prevDaysSource =
              prevTailData.tail_days?.length > 0
                ? prevTailData.tail_days
                : Object.keys(prevTailData.nurses[0]?.shifts || {}).map(Number);
            const prevDays = prevDaysSource
              .map(Number)
              .filter((day) => day >= 1 && day <= prevMonthLastDay)
              .sort((a, b) => a - b);

            if (prevDays.length === 0) return null;

            const firstDay = prevDays[0];
            const lastDay = prevDays[prevDays.length - 1];

            return (
              <div className="relative group/prev translate-x-[-1px] border border-b-0 border-dotted rounded-t-lg">
                {/* 캡션: X월 X~X일 근무보기 */}
                <div className="absolute -top-5 left-1/2 -translate-x-1/2 flex items-center justify-center pointer-events-none">
                  <div className="bg-white border border-dashed border-[#d1d1d6] rounded-full px-2.5  shadow-sm whitespace-nowrap">
                    <span className="text-[10px] font-bold text-[#8e8e93] ">
                      {prevMonth}월 {firstDay}~{lastDay}일
                    </span>
                  </div>
                </div>

                {/* 포괄적 감싸기 박스 (점선) - 레이아웃 밀림 방지를 위해 간격/패딩 제거 */}
                <div className="flex  border-[#D6D6DE] bg-[#F9F9FB] rounded-t-lg w-[203.5px] ">
                  {prevDays.map((day) => {
                    const prevDate = new Date(prevYear, prevMonth - 1, day);
                    const dayIndex = prevDate.getDay();
                    const dateStr = formatDate(prevYear, prevMonth, day);
                    const isHoliday = holidayDates.includes(dateStr);
                    const holiday = isHoliday
                      ? "holiday"
                      : dayIndex === 6
                        ? "sat"
                        : dayIndex === 0
                          ? "sun"
                          : "";

                    return (
                      <ThCalendarDay
                        key={`prev-${day}`}
                        day={day}
                        holiday={holiday}
                        weekDay={WEEKDAY_SHORT_SUN_FIRST[dayIndex]}
                        prefix="prev-"
                        selectionFocusOnClick={selectionFocusOnClick}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })()}
        {/* 현재 월 날짜 */}
        {Array.from({ length: lastDay }, (_, i) => {
          const dayIndex = (i + firstDayFactor) % 7;
          const dateStr = formatDate(currentYear, currentMonth, i + 1);
          const isHoliday = holidayDates.includes(dateStr);
          const holiday = isHoliday
            ? "holiday"
            : (i + firstDayFactor) % 7 === 6
              ? "sat"
              : (i + firstDayFactor) % 7 === 0
                ? "sun"
                : "";
          return (
            <ThCalendarDay
              key={i}
              day={i + 1}
              // isEveryFifth={!((i + 1) % 5)}
              holiday={holiday}
              weekDay={WEEKDAY_SHORT_SUN_FIRST[dayIndex]}
              selectionFocusOnClick={selectionFocusOnClick}
            />
          );
        })}
      </div>
    </RosterGrid>
  );
}

export default TableHeader;
